import { UAParser } from 'ua-parser-js'
import type { Browser } from '#shared/types/browsers'

/**
 * List of supported browsers.
 */
const supportedBrowserNames: Browser[] = ['Chrome', 'Edge', 'Firefox', 'Safari']

/**
 * This function checks the user agent and determines if we fully, partially, or
 * don't support this major version.
 *
 * Computed values here aren't needed, as the information isn't dynamic.
 */
export default function useBrowserSupport() {
  const userAgent = useRequestHeader('User-Agent') ?? undefined
  const parser = new UAParser(userAgent)
  const browser = parser.getBrowser()
  const browserName = browser.name as Browser
  const browserMajorVersion = Number(browser.major ?? 0)
  const supportedBrowsers = useSupportedBrowsers()

  /**
   * Used to determine if we recognize this browser and potentially display a
   * message. It doesn't mean the browser isn't suported.
   */
  const isRecognizedBrowser = supportedBrowserNames.includes(browserName) ?? false

  /**
   * The lowest full version of the browser that is supported.
   */
  const fullVersion = supportedBrowsers.value?.full?.[browserName as keyof typeof supportedBrowsers.value.full] ?? 0

  /**
   * The lowest partial version of the browser that is supported.
   */
  const partialVersion = supportedBrowsers.value?.partial?.[browserName as keyof typeof supportedBrowsers.value.partial] ?? 0

  /**
   * Checks if the current browser falls in the "fully supported" category.
   */
  const isFullySupported = Boolean(
    isRecognizedBrowser
    && supportedBrowsers.value?.full
    && browserName
    && browserMajorVersion >= fullVersion,
  )

  /**
   * Checks if the current browser falls in the "partially supported" category.
   */
  const isPartiallySupported = Boolean(
    isRecognizedBrowser
    && supportedBrowsers.value?.partial
    && browserName
    && browserMajorVersion >= partialVersion,
  )

  /**
   * Checks if the current browser is not supported.
   */
  const isNotSupported = !isRecognizedBrowser
    || !supportedBrowsers.value?.partial
    || !browserName
    || browserMajorVersion < partialVersion

  /**
   * Checks if the current browser is Chrome.
   */
  const isChrome = Boolean(browserName?.includes('Chrome'))

  /**
   * Checks if the current browser is Edge.
   */
  const isEdge = Boolean(browserName?.includes('Edge'))

  /**
   * Checks if the current browser is Firefox.
   */
  const isFirefox = Boolean(browserName?.includes('Firefox'))

  /**
   * Checks if the current browser is Safari.
   */
  const isSafari = Boolean(browserName?.includes('Safari'))

  return {
    browserName,
    browserMajorVersion,
    isRecognizedBrowser,
    isFullySupported,
    isPartiallySupported,
    isNotSupported,
    isChrome,
    isEdge,
    isFirefox,
    isSafari,
  }
}
